/**
 * Export dialog for MSSQL database .bacpac export.
 * Shows export progress and completion status.
 * Can be minimized to a status bar while operation continues in background.
 * In Personal mode, exports are limited to databases <= 1 GB.
 */

import { useEffect, useState } from 'react';
import { Database, Minus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { ProgressIndicator } from './ProgressIndicator';
import { PersonalModeBackupRestoreLimitMessage } from './PersonalModeBackupRestoreLimitMessage';
import { useBackupExport } from '../hooks/useBackupExport';
import { useOperationStatus } from '../hooks/useTaskProgress';
import { useBackupRestoreStore } from '../store/backupRestoreStore';
import { useProFeature } from '../../../core/licensing/hooks/useProFeature';
import { UpgradeDialog } from '../../../core/licensing';
import { checkPersonalModeLimits, fetchDatabaseSize, type PersonalModeLimitCheck } from '../utils/personalModeLimits';
import { toast } from '../../../components/ui/toaster';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
type ExportDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connectionId: string;
  databaseName: string;
};

export function ExportDialog({
  open,
  onOpenChange,
  connectionId,
  databaseName,
}: ExportDialogProps) {
  const [operationId, setOperationId] = useState<string | null>(null);
  const [isCheckingSize, setIsCheckingSize] = useState(false);
  const [limitCheck, setLimitCheck] = useState<PersonalModeLimitCheck | null>(null);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const operation = useOperationStatus(operationId);
  const { removeOperation, minimizeOperation, isMinimized } = useBackupRestoreStore();
  const { canUse: canUseFeature } = useProFeature('backup-restore-full');

  // Check if this operation is minimized
  const operationIsMinimized = operationId ? isMinimized(operationId) : false;

  const { exportDatabase, isExporting, reset } = useBackupExport({
    onSuccess: (result, opId) => {
      setOperationId(opId);

      if (result.cancelled) {
        // User cancelled, close the dialog
        onOpenChange(false);
        return;
      }

      if (!result.success) {
        toast.error({
          title: 'Export Failed',
          description: result.error || 'Unknown error occurred',
        });
      }
    },
    onError: (error) => {
      toast.error({
        title: 'Export Failed',
        description: error.message,
      });
    },
  });

  // Check database size and start export when dialog opens
  useEffect(() => {
    if (open && !operationId && !isExporting && !isCheckingSize && !limitCheck) {
      // If Pro user, skip size check and start immediately
      if (canUseFeature) {
        exportDatabase({
          connectionId,
          databaseName,
          filePath: '', // Will show save dialog
        });
      } else {
        // Personal mode: check database size first
        setIsCheckingSize(true);
        fetchDatabaseSize(connectionId, databaseName, 'mssql')
          .then((result) => {
            const check = checkPersonalModeLimits(result.success ? result.sizeInBytes ?? null : null);
            setLimitCheck(check);

            if (!check.exceedsLimit) {
              // Size is OK, proceed with export
              exportDatabase({
                connectionId,
                databaseName,
                filePath: '', // Will show save dialog
              });
            }
          })
          .finally(() => {
            setIsCheckingSize(false);
          });
      }
    }
  }, [open, operationId, isExporting, isCheckingSize, limitCheck, canUseFeature, connectionId, databaseName, exportDatabase]);

  // Show success toast when operation completes
  useEffect(() => {
    if (operation?.status === 'succeeded') {
      toast.success({
        title: 'Export Complete',
        description: `Database "${databaseName}" exported successfully`,
      });
    }
  }, [operation?.status, databaseName]);

  // Reset limit check when dialog closes
  useEffect(() => {
    if (!open) {
      setLimitCheck(null);
    }
  }, [open]);

  // Clean up on close (only when not minimized)
  const handleClose = () => {
    if (operationId && operation?.status !== 'inProgress') {
      removeOperation(operationId);
    }
    setOperationId(null);
    setLimitCheck(null);
    reset();
    onOpenChange(false);
  };

  // Minimize the dialog (operation continues in background)
  const handleMinimize = () => {
    if (operationId) {
      minimizeOperation(operationId);
      onOpenChange(false);
    }
  };

  // Determine if we can close:
  // - Not while isExporting is true (mutation is pending)
  // - Not while operation is actively in progress
  // - Always allow close if operation is complete (succeeded/failed/cancelled)
  // - Allow close if operation doesn't exist yet (initial state before export starts)
  // - Allow close if showing limit message
  const isComplete =
    operation?.status === 'succeeded' ||
    operation?.status === 'failed' ||
    operation?.status === 'cancelled';
  const showingLimitMessage = limitCheck?.exceedsLimit && !canUseFeature;
  const canClose = isComplete || (!isExporting && operation?.status !== 'inProgress') || showingLimitMessage || isCheckingSize;
  const canMinimize = !isComplete && (isExporting || operation?.status === 'inProgress');

  // Don't show dialog if minimized
  const dialogOpen = open && !operationIsMinimized;

  return (
    <>
      <Dialog open={dialogOpen} onOpenChange={(isOpen) => !isOpen && canClose && handleClose()}>
        <DialogContent data-testid="export-dialog">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
                <Database className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <DialogTitle>Export Data-tier Application</DialogTitle>
                <DialogDescription className="mt-1">
                  Exporting database &quot;{databaseName}&quot; to .bacpac file
                </DialogDescription>
              </div>
              {canMinimize && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={handleMinimize}
                      data-testid="export-dialog-minimize"
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    Minimize to status bar
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </DialogHeader>

          <div className="py-4">
            {isCheckingSize ? (
              <div className="flex flex-col items-center justify-center py-8 gap-3">
                <RandomDotmSpinner size={32} className="text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Checking database size...</p>
              </div>
            ) : showingLimitMessage && limitCheck ? (
              <PersonalModeBackupRestoreLimitMessage
                limitCheck={limitCheck}
                operationType="export"
                onUpgrade={() => setShowUpgradeDialog(true)}
                onCancel={handleClose}
              />
            ) : operation ? (
              <ProgressIndicator
                status={operation.status}
                progress={operation.progress}
                message={operation.message}
                error={operation.error}
                data-testid="export-progress"
              />
            ) : (
              <ProgressIndicator
                status="created"
                message="Preparing export..."
                data-testid="export-progress"
              />
            )}
          </div>

          {!showingLimitMessage && (
            <DialogFooter>
              <Button
                variant={isComplete ? 'default' : 'outline'}
                onClick={handleClose}
                disabled={!canClose}
                data-testid="export-dialog-close"
              >
                {isComplete ? 'Done' : 'Cancel'}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <UpgradeDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
      />
    </>
  );
}
